import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Nav, Spinner, Tab } from "react-bootstrap";
import { FiPlus, FiTrendingUp } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useKpiAction } from "@features/admin/kpi/hooks/useKpiAction";
import { useKpiOptions } from "../hooks/useKpiOptions";
import { KpiUnit } from "../types/kpiUnits";
import { KpiDataType } from "../types/kpiDataTypes";
import type { Kpi } from "../types/kpi";
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { SORT_BY_KPI_NAME, SORT_DIR_ASC } from "@core/constants/listSort";
import { fetchKpiAsync } from "../services/kpiSvc";
import { ENTRY_MODES, ENTRY_MODE_CALCULATED, ENTRY_MODE_MANUAL, ROLLUP_MODES, formatEntryModeLabel } from "../types/kpiEntryMode";
import KpiFormulaEditor from "./KpiFormulaEditor";
import KpiOptionsEditor from "./KpiOptionsEditor";
import KpiGridColumnsEditor from "./KpiGridColumnsEditor";
import KpiGridFixedRowsEditor from "./KpiGridFixedRowsEditor";
import KpiGridPreview from "./KpiGridPreview";
import {
  EMPTY_KPI_FORM,
  buildKpiPayload,
  dataTypeChangePatch,
  isManualOnlyDataTypeName,
  isRollupLockedNoneDataTypeName,
  resolveDataTypeName,
  shouldHideUnit,
  validateKpiForm,
  visibleKpiFormTabs,
  type KpiFormState,
  type KpiFormTab,
} from "../utils/kpiFormState";

type FieldErrors = Partial<Record<string, string[]>>;

const CreateKpiModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onChanged?: () => void;
}> = ({ show, onHide, onChanged }) => {
  const resolvedNationId = useScopeId() ?? 0;
  const [form, setForm] = useState<KpiFormState>(EMPTY_KPI_FORM);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const [activeTab, setActiveTab] = useState<KpiFormTab>("basics");
  const [availableKpis, setAvailableKpis] = useState<Kpi[]>([]);
  const showError = useApiError();
  const showValidation = useErrorModalStore((s) => s.showValidation);
  const { busy, doKpiCreate } = useKpiAction();
  const {
    kpiUnits,
    kpiUnitsLoading,
    kpiUnitsError,
    fetchKpiUnits,
    kpiDataTypes,
    kpiDataTypesLoading,
    kpiDataTypesError,
    fetchKpiDataTypes,
  } = useKpiOptions();

  useEffect(() => {
    fetchKpiUnits();
  }, [fetchKpiUnits]);

  useEffect(() => {
    fetchKpiDataTypes();
  }, [fetchKpiDataTypes]);

  useEffect(() => {
    if (!show || !resolvedNationId) return;
    let cancelled = false;
    (async () => {
      const items = await fetchAllPages<Kpi>((page) =>
        fetchKpiAsync(resolvedNationId, {
          all: true,
          page,
          isActive: true,
          sortBy: SORT_BY_KPI_NAME,
          sortDir: SORT_DIR_ASC,
        })
      );
      if (!cancelled) setAvailableKpis(items);
    })();
    return () => {
      cancelled = true;
    };
  }, [show, resolvedNationId]);

  const dataTypeName = resolveDataTypeName(form.data_type_id, kpiDataTypes);
  const tabs = useMemo(
    () => visibleKpiFormTabs(form, dataTypeName),
    [form, dataTypeName]
  );
  const hideUnit = shouldHideUnit(dataTypeName);
  const manualOnly = isManualOnlyDataTypeName(dataTypeName);
  const hideRollup = isRollupLockedNoneDataTypeName(dataTypeName);

  useEffect(() => {
    if (!tabs.includes(activeTab)) setActiveTab("basics");
  }, [tabs, activeTab]);

  const patchForm = (patch: Partial<KpiFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const resetForm = () => {
    setForm(EMPTY_KPI_FORM);
    setFormErrors({});
    setActiveTab("basics");
  };

  const handleSubmit = async () => {
    const messages = validateKpiForm(form, dataTypeName, kpiDataTypes);
    if (messages.length > 0) {
      showValidation(messages);
      return;
    }

    try {
      const payload = buildKpiPayload(form, dataTypeName);
      const response = await doKpiCreate(resolvedNationId, payload);

      if (response.isSuccess()) {
        showSuccess(response.getData()?.message);
        onHide();
        resetForm();
        onChanged?.();
      } else {
        const error = response.getError() as ErrorResponse;
        const fieldErrors = (error?.errors as FieldErrors) ?? {};
        setFormErrors(fieldErrors);
        const list = Object.values(fieldErrors)
          .flat()
          .filter((msg): msg is string => Boolean(msg));
        showValidation(list.length ? list : [error?.detail || "Failed to create KPI"]);
        showError(error);
      }
    } catch (error) {
      showError(error as ErrorResponse);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      data-testid="create-kpi-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0" data-testid="create-kpi-modal-header">
        <Modal.Title className="fw-semibold" data-testid="create-kpi-modal-title">
          <FiTrendingUp className="me-2" aria-hidden={true} />
          Create New KPI
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3" data-testid="create-kpi-modal-body">
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k as KpiFormTab)}
        >
          <Nav variant="tabs" className="mb-3">
            {tabs.includes("basics") ? (
              <Nav.Item>
                <Nav.Link eventKey="basics">Basics</Nav.Link>
              </Nav.Item>
            ) : null}
            {tabs.includes("formula") ? (
              <Nav.Item>
                <Nav.Link eventKey="formula">Formula</Nav.Link>
              </Nav.Item>
            ) : null}
            {tabs.includes("options") ? (
              <Nav.Item>
                <Nav.Link eventKey="options">Options</Nav.Link>
              </Nav.Item>
            ) : null}
            {tabs.includes("grid") ? (
              <Nav.Item>
                <Nav.Link eventKey="grid">Grid</Nav.Link>
              </Nav.Item>
            ) : null}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="basics">
              <Form data-testid="create-kpi-form">
                <Form.Group className="mb-3" controlId="kpiName">
                  <Form.Label>
                    KPI Name<span className="text-danger">*</span>
                    <InfoTooltip text="The name of this metric as it will appear in reports." />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter KPI name"
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    isInvalid={!!formErrors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="kpiDataType">
                  <Form.Label>
                    Data Type<span className="text-danger">*</span>
                    <InfoTooltip text="The kind of value entered: number, text, select, grid, etc." />
                  </Form.Label>
                  <Form.Select
                    value={form.data_type_id || 0}
                    onChange={(e) => {
                      const id = e.target.value === "" ? "" : Number(e.target.value);
                      const name = resolveDataTypeName(id, kpiDataTypes);
                      patchForm({
                        data_type_id: id,
                        ...dataTypeChangePatch(name),
                      });
                    }}
                    isInvalid={!!formErrors.data_type_id}
                    data-testid="kpi-data-type-select"
                  >
                    <option value={0}>Select a data type</option>
                    {kpiDataTypesLoading && <option value={0}>Loading data types...</option>}
                    {kpiDataTypesError && <option disabled>Failed to load data types</option>}
                    {!kpiDataTypesLoading &&
                      !kpiDataTypesError &&
                      kpiDataTypes.map((dt: KpiDataType) => (
                        <option key={dt.data_type_id} value={dt.data_type_id}>
                          {dt.label || dt.data_type_name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>

                {!hideUnit ? (
                  <Form.Group className="mb-3" controlId="kpiUnit">
                    <Form.Label>
                      Unit<span className="text-danger">*</span>
                      <InfoTooltip text="How this KPI is measured, e.g. Count for numbers." />
                    </Form.Label>
                    <Form.Select
                      value={form.unit_id || 0}
                      onChange={(e) =>
                        patchForm({
                          unit_id: e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      isInvalid={!!formErrors.unit_id}
                      data-testid="kpi-unit-select"
                    >
                      <option value={0}>Select a unit</option>
                      {kpiUnitsLoading && <option value={0}>Loading units...</option>}
                      {kpiUnitsError && <option disabled>Failed to load units</option>}
                      {!kpiUnitsLoading &&
                        !kpiUnitsError &&
                        kpiUnits.map((u: KpiUnit) => (
                          <option key={u.unit_id} value={u.unit_id}>
                            {u.unit_name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                ) : null}

                <Form.Group className="mb-3" controlId="kpiEntryMode">
                  <Form.Label>Entry mode</Form.Label>
                  <Form.Select
                    value={manualOnly ? ENTRY_MODE_MANUAL : form.entry_mode}
                    disabled={manualOnly}
                    onChange={(e) => patchForm({ entry_mode: e.target.value })}
                    data-testid="kpi-entry-mode-select"
                  >
                    {(manualOnly ? ([ENTRY_MODE_MANUAL] as const) : ENTRY_MODES).map(
                      (mode) => (
                        <option key={mode} value={mode}>
                          {formatEntryModeLabel(mode)}
                        </option>
                      )
                    )}
                  </Form.Select>
                  {manualOnly ? (
                    <Form.Text muted>
                      Grid, Select, and Multiselect KPIs are always entered
                      manually.
                    </Form.Text>
                  ) : null}
                </Form.Group>

                {!hideRollup ? (
                  <Form.Group className="mb-3" controlId="kpiRollupMode">
                    <Form.Label>Rollup mode</Form.Label>
                    <Form.Select
                      value={form.rollup_mode}
                      onChange={(e) =>
                        patchForm({ rollup_mode: e.target.value })
                      }
                    >
                      {ROLLUP_MODES.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ) : null}

                {!manualOnly &&
                (form.entry_mode || "").toUpperCase() === ENTRY_MODE_CALCULATED ? (
                  <Form.Group className="mb-3" controlId="kpiDecimalPlaces">
                    <Form.Label>Decimal places</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={6}
                      value={form.decimal_places === "" ? "" : form.decimal_places}
                      onChange={(e) =>
                        patchForm({
                          decimal_places:
                            e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                ) : null}

                <Form.Group className="mb-3" controlId="kpiDescription">
                  <Form.Label>
                    Description
                    <InfoTooltip text="Optional notes about what this KPI measures." />
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter KPI description"
                    value={form.description}
                    onChange={(e) => patchForm({ description: e.target.value })}
                  />
                </Form.Group>
              </Form>
            </Tab.Pane>

            <Tab.Pane eventKey="formula">
              <KpiFormulaEditor
                operation={form.operation}
                operands={form.operands}
                availableKpis={availableKpis}
                onChangeOperation={(operation) => patchForm({ operation })}
                onChangeOperands={(operands) => patchForm({ operands })}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="options">
              <KpiOptionsEditor
                options={form.options}
                minSelections={form.min_selections}
                maxSelections={form.max_selections}
                singleSelect={dataTypeName.toUpperCase() === "SELECT"}
                onChangeOptions={(options) => patchForm({ options })}
                onChangeMin={(min_selections) => patchForm({ min_selections })}
                onChangeMax={(max_selections) => patchForm({ max_selections })}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="grid">
              <KpiGridColumnsEditor
                columns={form.grid_columns}
                gridMinRows={form.grid_min_rows}
                gridMaxRows={form.grid_max_rows}
                gridShowColumnTotals={form.grid_show_column_totals}
                gridShowRowTotals={form.grid_show_row_totals}
                dataTypes={kpiDataTypes}
                onChangeColumns={(grid_columns) => patchForm({ grid_columns })}
                onChangeMinRows={(grid_min_rows) => patchForm({ grid_min_rows })}
                onChangeMaxRows={(grid_max_rows) => patchForm({ grid_max_rows })}
                onChangeShowColumnTotals={(grid_show_column_totals) =>
                  patchForm({ grid_show_column_totals })
                }
                onChangeShowRowTotals={(grid_show_row_totals) =>
                  patchForm({ grid_show_row_totals })
                }
              />
              <KpiGridFixedRowsEditor
                rows={form.grid_fixed_rows}
                onChange={(grid_fixed_rows) => patchForm({ grid_fixed_rows })}
              />
              <KpiGridPreview
                columns={form.grid_columns}
                fixedRows={form.grid_fixed_rows}
                gridMinRows={form.grid_min_rows}
                gridShowColumnTotals={form.grid_show_column_totals}
                gridShowRowTotals={form.grid_show_row_totals}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <div className="d-flex justify-content-end mt-3">
          <Button
            variant="primary"
            size="lg"
            className="btn-action"
            onClick={handleSubmit}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? (
              <>
                <Spinner animation="border" className="me-2" size="sm" />
                Creating KPI...
              </>
            ) : (
              <>
                <FiPlus className="me-2" />
                Create KPI
              </>
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateKpiModal;
